import { useState, useRef, ChangeEvent } from 'react'
import type { Book } from '@/types'
import { supabase } from '../../lib/supabase'
import { FIELD_LIMITS, truncateField, normalizeToArray, validateAuthors, validateCategories } from '../../utils/validation'
import { useModalBehavior } from '../../hooks/useModalBehavior'
import TagInput from '../common/TagInput'
import styles from './ScannedBookDetailModal.module.css'
import {
    FORMAT_OPTIONS,
    STATUS_OPTIONS,
    PROGRESS_OPTIONS,
    getFormatLabel,
    getStatusLabel,
    getProgressLabel,
} from '@/constants/bookOptions'



interface ScannedBookDetailModalProps {
    book?: Partial<Book>
    onClose: () => void
    onSave?: (book: Book) => void
    onUpdate?: (book: Partial<Book>) => void
    existingAuthors?: string[]
    existingPublishers?: string[]
    existingLocations?: string[]
    existingSeries?: string[]
    existingCategories?: string[]
    existingLanguages?: string[]
}

interface FormData {
    title: string
    author: string[]
    notes: string
    synopsis: string
    description: string
    page_count: string | number
    format: string
    status: string
    progress: string
    publisher: string
    location: string
    series: string
    series_number: string | number
    category: string[]
    language: string
}

export default function ScannedBookDetailModal({
    book,
    onClose,
    onSave,
    onUpdate,
    existingAuthors = [],
    existingPublishers = [],
    existingLocations = [],
    existingSeries = [],
    existingCategories = [],
    existingLanguages = [],
}: Readonly<ScannedBookDetailModalProps>) {
    const [formData, setFormData] = useState<FormData>({
        title: book?.title || '',
        author: normalizeToArray(book?.author),
        notes: book?.notes || '',
        synopsis: book?.synopsis || '',
        description: book?.description || '',
        page_count: book?.page_count ?? '',
        format: book?.format || 'physical',
        status: book?.status || 'owned',
        progress: book?.progress || 'not_read',
        publisher: book?.publisher || '',
        location: book?.location || '',
        series: book?.series || '',
        series_number: book?.series_number ?? '',
        category: normalizeToArray(book?.category),
        language: book?.language || '',
    })

    const [saving, setSaving] = useState(false)
    const [showPublisherSuggestions, setShowPublisherSuggestions] = useState(false)
    const [showLocationSuggestions, setShowLocationSuggestions] = useState(false)
    const [showSeriesSuggestions, setShowSeriesSuggestions] = useState(false)
    const [showLanguageSuggestions, setShowLanguageSuggestions] = useState(false)

    const modalRef = useRef<HTMLDivElement>(null)
    const publisherInputRef = useRef<HTMLDivElement>(null)
    const locationInputRef = useRef<HTMLDivElement>(null)
    const seriesInputRef = useRef<HTMLDivElement>(null)
    const languageInputRef = useRef<HTMLDivElement>(null)

    useModalBehavior({
        modalRef,
        onClose,
        suggestionRefs: [
            { ref: publisherInputRef, setter: setShowPublisherSuggestions },
            { ref: locationInputRef, setter: setShowLocationSuggestions },
            { ref: seriesInputRef, setter: setShowSeriesSuggestions },
            { ref: languageInputRef, setter: setShowLanguageSuggestions },
        ],
    })

    const handleChange = (field: keyof FormData, value: string | string[] | number) => {
        let finalValue = value
        const b = FIELD_LIMITS.books

        if (field === 'author') {
            finalValue = validateAuthors(value as string[])
        } else if (field === 'category') {
            finalValue = validateCategories(value as string[])
        } else if (field === 'notes') {
            finalValue = truncateField(String(value), b.notes)
        } else if (field === 'synopsis') {
            finalValue = truncateField(String(value), b.synopsis)
        } else if (field === 'description') {
            finalValue = truncateField(String(value), b.description)
        } else if (field === 'publisher') {
            finalValue = truncateField(String(value), b.publisher)
        } else if (field === 'location') {
            finalValue = truncateField(String(value), b.location)
        } else if (field === 'series') {
            finalValue = truncateField(String(value), b.series)
        }

        setFormData((prev) => ({ ...prev, [field]: finalValue }))
    }

    const handleSeriesNumberChange = (value: string) => {
        if (value === '' || /^\d+$/.test(value)) {
            setFormData((prev) => ({ ...prev, series_number: value }))
        }
    }

    const handlePageCountChange = (value: string) => {
        if (value === '' || /^\d+$/.test(value)) {
            setFormData((prev) => ({ ...prev, page_count: value }))
        }
    }

    const handleSave = async () => {
        setSaving(true)

        const {
            data: { user },
        } = await supabase.auth.getUser()
        if (!user) {
            console.error('No authenticated user')
            setSaving(false)
            return
        }

        const b = FIELD_LIMITS.books
        const finalData = {
            ...formData,
            author: validateAuthors(formData.author),
            category: validateCategories(formData.category),
            notes: truncateField(formData.notes, b.notes),
            synopsis: truncateField(formData.synopsis, b.synopsis),
            description: truncateField(formData.description, b.description),
            publisher: truncateField(formData.publisher, b.publisher),
            location: truncateField(formData.location, b.location),
            series: truncateField(formData.series, b.series),
            language: truncateField(formData.language, b.language),
        }

        const newBook = {
            user_id: user.id,
            title: finalData.title,
            author: finalData.author,
            notes: finalData.notes,
            synopsis: finalData.synopsis || null,
            description: finalData.description || null,
            page_count: finalData.page_count === '' ? null : Number.parseInt(String(finalData.page_count), 10),
            format: finalData.format,
            status: finalData.status,
            progress: finalData.progress,
            publisher: finalData.publisher || null,
            location: finalData.location || null,
            series: finalData.series || null,
            series_number: finalData.series_number === '' ? null : Number.parseInt(String(finalData.series_number), 10),
            category: finalData.category,
            language: finalData.language || null,
        }

        const { data, error } = await supabase.from('books').insert([newBook]).select()

        setSaving(false)

        if (!error && data) {
            onSave?.(data[0] as Book)
            onClose()
        }
    }

    const handleKeepEditing = () => {
        if (onUpdate) {
            const b = FIELD_LIMITS.books
            const updatedBook: Partial<Book> = {
                ...book,
                title: formData.title,
                author: validateAuthors(formData.author),
                notes: truncateField(formData.notes, b.notes),
                synopsis: truncateField(formData.synopsis, b.synopsis),
                description: truncateField(formData.description, b.description),
                page_count: formData.page_count === '' ? null : Number.parseInt(String(formData.page_count), 10),
                format: formData.format,
                status: formData.status,
                progress: formData.progress,
                publisher: truncateField(formData.publisher, b.publisher),
                location: truncateField(formData.location, b.location),
                series: truncateField(formData.series, b.series),
                series_number: formData.series_number === '' ? null : Number.parseInt(String(formData.series_number), 10),
                category: validateCategories(formData.category),
                language: truncateField(formData.language, b.language),
            }
            onUpdate(updatedBook)
        }
        onClose()
    }

    const filteredPublishers = existingPublishers.filter(
        (e) => e && e.toLowerCase().includes(formData.publisher.toLowerCase()) && e !== formData.publisher
    )
    const filteredLocations = existingLocations.filter(
        (u) => u && u.toLowerCase().includes(formData.location.toLowerCase()) && u !== formData.location
    )
    const filteredSeries = existingSeries.filter((s) => s && s.toLowerCase().includes(formData.series.toLowerCase()) && s !== formData.series)
    const filteredLanguages = existingLanguages.filter((i) => i && i.toLowerCase().includes(formData.language.toLowerCase()) && i !== formData.language)

    return (
        <div className={styles.scannedBookDetailOverlay}>
            <div className={styles.scannedBookDetailModal} ref={modalRef}>
                <div className={styles.scannedBookDetailHeader}>
                    <div className={styles.scannedBookDetailTitleSection}>
                        <span className={styles.scannedBookDetailIcon}>✨</span>
                        <div>
                            <h3 className={styles.scannedBookDetailTitle}>Añadir a la biblioteca</h3>
                            <p className={styles.scannedBookDetailSubtitle}>Revisa y completa los datos antes de guardar</p>
                        </div>
                    </div>
                    <button className={styles.scannedBookDetailClose} onClick={onClose} aria-label="Cerrar">
                        ✕
                    </button>
                </div>

                <div className={styles.scannedBookDetailBody}>
                    {/* Title and Author */}
                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                        <label htmlFor="scanned-title">Título</label>
                        <input
                            id="scanned-title"
                            type="text"
                            value={formData.title}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('title', e.target.value)}
                            placeholder="Título del libro"
                            className={styles.formInput}
                        />
                    </div>

                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                        <label htmlFor="scanned-author">Autor</label>
                        <TagInput
                            id="scanned-author"
                            value={formData.author}
                            onChange={(vals) => handleChange('author', vals)}
                            suggestions={existingAuthors}
                            placeholder="Añadir autores..."
                            maxTags={FIELD_LIMITS.books.author_max_items}
                            maxLength={FIELD_LIMITS.books.author_max_length}
                        />
                    </div>

                    {/* Serie Fields */}
                    <div className={`${styles.formRow} ${styles.seriesRow}`}>
                        <div className={styles.formGroup} ref={seriesInputRef}>
                            <label htmlFor="series">Serie</label>
                            <div className={styles.comboboxWrapper}>
                                <input
                                    type="text"
                                    id="series"
                                    className={styles.formInput}
                                    value={formData.series}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        handleChange('series', truncateField(e.target.value, FIELD_LIMITS.books.series))
                                        setShowSeriesSuggestions(true)
                                    }}
                                    onFocus={() => setShowSeriesSuggestions(true)}
                                    placeholder="Nombre de la series o saga"
                                    maxLength={FIELD_LIMITS.books.series}
                                    autoComplete="off"
                                />
                                {showSeriesSuggestions && filteredSeries.length > 0 && (
                                    <div className={styles.suggestionsList}>
                                        {filteredSeries.map((series) => (
                                            <button
                                                key={series}
                                                type="button"
                                                className={styles.suggestionItem}
                                                onClick={() => {
                                                    handleChange('series', series)
                                                    setShowSeriesSuggestions(false)
                                                }}
                                            >
                                                {series}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.formGroup}>
                            <label htmlFor="scanned-series-number">Nº</label>
                            <input
                                id="scanned-series-number"
                                type="text"
                                inputMode="numeric"
                                pattern="\d*"
                                value={formData.series_number}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => handleSeriesNumberChange(e.target.value)}
                                placeholder="#"
                                className={styles.formInput}
                            />
                        </div>
                    </div>

                    {/* Synopsis */}
                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                        <label htmlFor="synopsis">📄 Sinopsis</label>
                        <textarea
                            id="synopsis"
                            className={styles.formTextarea}
                            value={formData.synopsis}
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                                handleChange('synopsis', truncateField(e.target.value, FIELD_LIMITS.books.synopsis))
                            }
                            placeholder="Breve resumen del libro..."
                            rows={3}
                            maxLength={FIELD_LIMITS.books.synopsis}
                        />
                        <span className={`char-counter ${formData.synopsis.length >= FIELD_LIMITS.books.synopsis * 0.9 ? 'warning' : ''}`}>
                            {formData.synopsis.length}/{FIELD_LIMITS.books.synopsis}
                        </span>
                    </div>

                    {/* Description */}
                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                        <label htmlFor="description">📋 Descripción</label>
                        <textarea
                            id="description"
                            className={styles.formTextarea}
                            value={formData.description}
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                                handleChange('description', truncateField(e.target.value, FIELD_LIMITS.books.description))
                            }
                            placeholder="Descripción adicional del libro..."
                            rows={3}
                            maxLength={FIELD_LIMITS.books.description}
                        />
                        <span className={`char-counter ${formData.description.length >= FIELD_LIMITS.books.description * 0.9 ? 'warning' : ''}`}>
                            {formData.description.length}/{FIELD_LIMITS.books.description}
                        </span>
                    </div>

                    {/* Page Count */}
                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                        <label htmlFor="scanned-page-count">📖 Número de Páginas</label>
                        <input
                            id="scanned-page-count"
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            value={formData.page_count}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => handlePageCountChange(e.target.value)}
                            placeholder="Ej: 350"
                            className={styles.formInput}
                        />
                    </div>

                    {/* Categoria and Language */}
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="scanned-category">Categoría</label>
                            <TagInput
                                id="scanned-category"
                                value={formData.category}
                                onChange={(vals) => handleChange('category', vals)}
                                suggestions={existingCategories}
                                placeholder="Añadir categorías..."
                                maxTags={FIELD_LIMITS.books.category_max_items}
                                maxLength={FIELD_LIMITS.books.category}
                            />
                        </div>
                        <div className={styles.formGroup} ref={languageInputRef}>
                            <label htmlFor="language">Idioma</label>
                            <div className={styles.comboboxWrapper}>
                                <input
                                    type="text"
                                    id="language"
                                    className={styles.formInput}
                                    value={formData.language}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        handleChange('language', truncateField(e.target.value, FIELD_LIMITS.books.language))
                                        setShowLanguageSuggestions(true)
                                    }}
                                    onFocus={() => setShowLanguageSuggestions(true)}
                                    placeholder="Idioma..."
                                    maxLength={FIELD_LIMITS.books.language}
                                    autoComplete="off"
                                />
                                {showLanguageSuggestions && filteredLanguages.length > 0 && (
                                    <div className={styles.suggestionsList}>
                                        {filteredLanguages.map((lang) => (
                                            <button
                                                key={lang}
                                                type="button"
                                                className={styles.suggestionItem}
                                                onClick={() => {
                                                    handleChange('language', lang)
                                                    setShowLanguageSuggestions(false)
                                                }}
                                            >
                                                {lang}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Publisher and Location */}
                    <div className={styles.formRow}>
                        <div className={styles.formGroup} ref={publisherInputRef}>
                            <label htmlFor="publisher">Editorial</label>
                            <div className={styles.comboboxWrapper}>
                                <input
                                    type="text"
                                    id="publisher"
                                    className={styles.formInput}
                                    value={formData.publisher}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        handleChange('publisher', truncateField(e.target.value, FIELD_LIMITS.books.publisher))
                                        setShowPublisherSuggestions(true)
                                    }}
                                    onFocus={() => setShowPublisherSuggestions(true)}
                                    placeholder="Nombre de la editorial..."
                                    maxLength={FIELD_LIMITS.books.publisher}
                                    autoComplete="off"
                                />
                                {showPublisherSuggestions && filteredPublishers.length > 0 && (
                                    <div className={styles.suggestionsList}>
                                        {filteredPublishers.map((publisher) => (
                                            <button
                                                key={publisher}
                                                type="button"
                                                className={styles.suggestionItem}
                                                onClick={() => {
                                                    handleChange('publisher', publisher)
                                                    setShowPublisherSuggestions(false)
                                                }}
                                            >
                                                {publisher}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.formGroup} ref={locationInputRef}>
                            <label htmlFor="location">Ubicación</label>
                            <div className={styles.comboboxWrapper}>
                                <input
                                    type="text"
                                    id="location"
                                    className={styles.formInput}
                                    value={formData.location}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        handleChange('location', truncateField(e.target.value, FIELD_LIMITS.books.location))
                                        setShowLocationSuggestions(true)
                                    }}
                                    onFocus={() => setShowLocationSuggestions(true)}
                                    placeholder="Dónde está el libro..."
                                    maxLength={FIELD_LIMITS.books.location}
                                    autoComplete="off"
                                />
                                {showLocationSuggestions && filteredLocations.length > 0 && (
                                    <div className={styles.suggestionsList}>
                                        {filteredLocations.map((location) => (
                                            <div
                                                key={location}
                                                className={styles.suggestionItem}
                                                onClick={() => {
                                                    handleChange('location', location)
                                                    setShowLocationSuggestions(false)
                                                }}
                                            >
                                                {location}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>Estado</label>
                            <select
                                value={formData.status}
                                onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChange('status', e.target.value)}
                                className={styles.formSelect}
                            >
                                {STATUS_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                        {getStatusLabel(opt)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.formGroup}>
                            <label>Progreso</label>
                            <select
                                value={formData.progress}
                                onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChange('progress', e.target.value)}
                                className={styles.formSelect}
                            >
                                {PROGRESS_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                        {getProgressLabel(opt)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                        <label>Formato</label>
                        <select
                            value={formData.format}
                            onChange={(e: ChangeEvent<HTMLSelectElement>) => handleChange('format', e.target.value)}
                            className={styles.formSelect}
                        >
                            {FORMAT_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                    {getFormatLabel(opt)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                        <label>Notas</label>
                        <textarea
                            value={formData.notes}
                            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChange('notes', truncateField(e.target.value, FIELD_LIMITS.books.notes))}
                            placeholder="Añadir notas personales..."
                            className={styles.formTextarea}
                            rows={3}
                            maxLength={FIELD_LIMITS.books.notes}
                        />
                        <span className={`char-counter ${formData.notes.length >= FIELD_LIMITS.books.notes * 0.9 ? 'warning' : ''}`}>
                            {formData.notes.length}/{FIELD_LIMITS.books.notes}
                        </span>
                    </div>
                </div>

                <div className={styles.scannedBookDetailFooter}>
                    <button className={styles.btnText} onClick={onClose} disabled={saving}>
                        Cancelar
                    </button>
                    <button className="btn-secondary" onClick={handleKeepEditing} disabled={saving || !formData.title}>
                        ✏️ Seguir editando
                    </button>
                    <button className={styles.btnPrimary} onClick={handleSave} disabled={saving || !formData.title}>
                        {saving ? <span className="spinner small"></span> : '💾 Guardar en biblioteca'}
                    </button>
                </div>
            </div>
        </div>
    )
}
